import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePolicyFrameworks, usePolicyFrameworkMutations } from '../../../hooks/usePolicyFrameworks';
import { useNotifications } from '../../NotificationSystem';
import './SetupWizardSteps.css';

const Step5PolicyFramework = ({ stepData, onComplete, onValidation, userData, userName, structure }) => {
  const { notify } = useNotifications();
  const { t } = useTranslation('setup');
  const [loading, setLoading] = useState(false);
  const organizationId = structure?.organizations?.[0]?.id || null;
  const { frameworks, loading: frameworksLoading } = usePolicyFrameworks(organizationId);
  const { createFramework } = usePolicyFrameworkMutations();

  useEffect(() => {
    if (frameworks && frameworks.length >= 1) {
      onComplete({ frameworks: frameworks.length, requirementsMet: true });
    }
  }, [frameworks]);

  const handleCreateBasicFramework = async () => {
    if (!organizationId) {
      notify.error(t('step5.notifications.missingOrganization'), t('step5.notifications.missingOrganizationTitle'));
      return;
    }

    setLoading(true);
    try {
      await createFramework({
        name: t('step5.defaultFramework.name'),
        description: t('step5.defaultFramework.description'),
        code: 'POL-001',
        scope: 'hospital-wide',
        authority: t('step5.defaultFramework.authority'),
        status: 'active',
        reviewFrequency: 'annual',
        complianceMandatory: true,
        organizationId
      }, userData?.uid || 'system', userName || 'System');
      
      notify.success(t('step5.notifications.createdSuccess'), t('step5.notifications.createdSuccessTitle'));
      onComplete({ frameworks: 1, requirementsMet: true });
    } catch (error) {
      console.error('Error creating framework:', error);
      notify.error(t('step5.notifications.createFailed'), t('step5.notifications.createFailedTitle'));
    } finally {
      setLoading(false);
    }
  };

  const requirements = {
    frameworks: (frameworks || []).length >= 1
  };

  return (
    <div className="step-content">
      <div className="step-section">
        <h3>{t('step5.title')}</h3>
        <p>{t('step5.description')}</p>
      </div>

      {!organizationId && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '6px' }}>
          {t('step5.completeStep1Warning')}
        </div>
      )}

      <div className="quick-setup-section">
        <div className="quick-setup-option" style={{ cursor: loading || !organizationId ? 'not-allowed' : 'pointer', opacity: loading || !organizationId ? 0.6 : 1 }}>
          <div style={{ textAlign: 'center' }}>
            <h3>{t('step5.quickSetupTitle')}</h3>
            <p>{t('step5.quickSetupDescription')}</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCreateBasicFramework}
              disabled={loading || !organizationId}
              style={{ marginTop: '16px' }}
            >
              {loading ? t('step5.creating') : t('step5.createBasicFramework')}
            </button>
          </div>
        </div>
      </div>

      <div className="requirements-section" style={{ marginTop: '32px' }}>
        <h3>{t('step5.frameworksTitle')}</h3>
        <div className="requirements-list">
          <div className={`requirement-item ${requirements.frameworks ? 'met' : 'pending'}`}>
            {requirements.frameworks ? <CheckCircle size={20} color="#4caf50" /> : <AlertCircle size={20} color="#ff9800" />}
            <span>{t('step5.frameworksCount', { count: (frameworks || []).length })}</span>
          </div>
        </div>

        {requirements.frameworks && (
          <div className="success-message" style={{ marginTop: '20px' }}>
            {t('step5.minimumMet')}
          </div>
        )}
      </div>
    </div>
  );
};

export default Step5PolicyFramework;
